/**
 * ImpersonationAuditLog
 * Shows a history of all impersonation events performed by admins in the
 * Course Scheduler. Visible only to Scheduler.Admin / Survey.Admin.
 */

import { useState, useEffect } from 'react';
import { schedulerApi, ImpersonationEvent } from '../../services/scheduler-api';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

const TH = {
  padding: '10px 14px',
  textAlign: 'left' as const,
  color: 'var(--text-secondary)',
  fontWeight: '600' as const,
  fontSize: '12px',
  whiteSpace: 'nowrap' as const,
  borderBottom: '2px solid var(--card-border)',
};

const TD = {
  padding: '10px 14px',
  fontSize: '13px',
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--card-border)',
};

function fmtDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function ImpersonationAuditLog({ alwaysOpen }: { alwaysOpen?: boolean } = {}) {
  const [events, setEvents] = useState<ImpersonationEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (alwaysOpen && !hasFetched) {
      setHasFetched(true);
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alwaysOpen]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !hasFetched) {
      setHasFetched(true);
      load();
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schedulerApi.getImpersonationLog();
      setEvents(data.events ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const filtered = events.filter(e => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      e.admin_email.toLowerCase().includes(q) ||
      e.admin_name.toLowerCase().includes(q) ||
      e.instructor_email.toLowerCase().includes(q) ||
      e.instructor_name.toLowerCase().includes(q)
    );
  });

  const showContent = alwaysOpen || expanded;

  return (
    <div style={{ background: 'var(--card-bg)', border: alwaysOpen ? 'none' : '1px solid var(--card-border)', borderRadius: alwaysOpen ? '0' : '12px', overflow: 'hidden' }}>
      {/* Clickable header — hidden in alwaysOpen mode */}
      {!alwaysOpen && (
        <button
          onClick={toggle}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>🕵️ Impersonation Audit Log</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {hasFetched && !loading ? `${events.length} event${events.length !== 1 ? 's' : ''}` : 'Admin impersonation history'}
            </div>
          </div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '14px', flexShrink: 0, marginLeft: '0.5rem' }}>{expanded ? '▲' : '▼'}</span>
        </button>
      )}

      {/* Content */}
      {showContent && (
        <div style={{ borderTop: alwaysOpen ? 'none' : '1px solid var(--card-border)', padding: '1.25rem' }}>
          {loading && <p style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center', padding: '1rem 0' }}>Loading audit log…</p>}
          {error && <p style={{ color: '#f87171', fontSize: '13px' }}>❌ {error}</p>}
          {!loading && !error && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <input
                  placeholder="Filter by admin or instructor…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '12px', flex: 1, minWidth: '140px' }}
                />
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{filtered.length} event{filtered.length !== 1 ? 's' : ''}</span>
                  <button onClick={load} style={{ padding: '5px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--card-border)', borderRadius: '6px', fontSize: '11px', color: 'var(--text-primary)', cursor: 'pointer' }}>↻ Refresh</button>
                </div>
              </div>
              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: '32px', marginBottom: '0.5rem' }}>🕵️</div>
                  <p style={{ margin: 0, fontSize: '13px' }}>{events.length === 0 ? 'No impersonation events recorded yet.' : 'No events match your filter.'}</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={TH}>When</th>
                        <th style={TH}>Admin</th>
                        <th style={TH}>Instructor Impersonated</th>
                        <th style={TH}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(ev => (
                        <tr key={ev.id}>
                          <td style={{ ...TD, color: 'var(--text-secondary)', fontSize: '12px', whiteSpace: 'nowrap' }}>{fmtDate(ev.started_at)}</td>
                          <td style={TD}>
                            <div style={{ fontWeight: '500' }}>{ev.admin_name || ev.admin_email}</div>
                            {ev.admin_name && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ev.admin_email}</div>}
                          </td>
                          <td style={TD}>
                            <div style={{ fontWeight: '500' }}>{ev.instructor_name || ev.instructor_email}</div>
                            {ev.instructor_name && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ev.instructor_email}</div>}
                          </td>
                          <td style={TD}>
                            <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: ev.action === 'start' ? 'rgba(250,204,21,0.15)' : 'rgba(148,163,184,0.15)', color: ev.action === 'start' ? '#facc15' : 'var(--text-secondary)' }}>
                              {ev.action === 'start' ? '▶ Started' : '⏹ Ended'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
